import React, { useMemo, useState } from "react";

function BuyerRetentionPage({ users, dateFilteredAuctions }) {
  const [buyerSearch, setBuyerSearch] = useState("");

  const getTime = (value) => {
    if (!value) return 0;
    if (value?.seconds) return value.seconds * 1000;
    return Number(value || 0);
  };

  const formatDate = (value) => {
    const time = getTime(value);
    if (!time) return "—";
    return new Date(time).toLocaleString();
  };

  const formatKWD = (value) => {
    return `${Number(value || 0).toFixed(3)} KWD`;
  };

  const buyerRetention = useMemo(() => {
    const buyerMap = {};

    dateFilteredAuctions
      .filter((auction) => auction.currentWinner || auction.winner)
      .forEach((auction) => {
        const buyerName = auction.currentWinner || auction.winner || "Unknown Buyer";
        const buyerKey = buyerName;

        if (!buyerMap[buyerKey]) {
          const matchedUser = users.find(
            (user) =>
              user.username === buyerName ||
              user.email === buyerName
          );

          buyerMap[buyerKey] = {
            buyerKey,
            buyerName,
            email: matchedUser?.email || "—",
            auctionsWon: 0,
            totalWinningValue: 0,
            firstWinDate: 0,
            lastWinDate: 0,
            latestWonAuction: "",
            highestWinningBid: 0,
          };
        }

        const winTime =
          getTime(auction.closedAt) ||
          getTime(auction.endedAt) ||
          getTime(auction.createdAt);

        buyerMap[buyerKey].auctionsWon += 1;
        buyerMap[buyerKey].totalWinningValue += Number(auction.currentBid || 0);
        buyerMap[buyerKey].latestWonAuction = auction.title || "Untitled Auction";

        buyerMap[buyerKey].highestWinningBid = Math.max(
          Number(buyerMap[buyerKey].highestWinningBid || 0),
          Number(auction.currentBid || 0)
        );

        if (
          winTime &&
          (!buyerMap[buyerKey].firstWinDate ||
            winTime < buyerMap[buyerKey].firstWinDate)
        ) {
          buyerMap[buyerKey].firstWinDate = winTime;
        }

        if (winTime && winTime > buyerMap[buyerKey].lastWinDate) {
          buyerMap[buyerKey].lastWinDate = winTime;
        }
      });

    return Object.values(buyerMap)
      .map((buyer) => {
        const daysSinceLastWin = buyer.lastWinDate
          ? Math.floor((Date.now() - buyer.lastWinDate) / (1000 * 60 * 60 * 24))
          : null;

          let retentionStatus = "New Buyer";

          if (buyer.auctionsWon >= 2 && buyer.auctionsWon <= 4) {
            retentionStatus = "Repeat Buyer";
          }
  
          if (buyer.auctionsWon >= 5 && buyer.auctionsWon <= 9) {
            retentionStatus = "Loyal Buyer";
          }
  
          if (buyer.auctionsWon >= 10) {
            retentionStatus = "VIP Buyer";
          }
  
          if (buyer.totalWinningValue >= 50) {
            retentionStatus = "High-Value Buyer";
          }
  
          if (daysSinceLastWin !== null && daysSinceLastWin >= 30) {
            retentionStatus = "Dormant Buyer";
          }

        return {
          ...buyer,
          daysSinceLastWin,
          retentionStatus,
        };
      })
      .sort((a, b) => Number(b.auctionsWon || 0) - Number(a.auctionsWon || 0));
  }, [users, dateFilteredAuctions]);

  const filteredBuyers = buyerRetention.filter((buyer) => {
    const searchText = buyerSearch.toLowerCase().trim();

    if (!searchText) return true;

    return (
      String(buyer.buyerName || "").toLowerCase().includes(searchText) ||
      String(buyer.email || "").toLowerCase().includes(searchText)
    );
  });

  const repeatBuyers = buyerRetention.filter(
    (buyer) =>
      buyer.retentionStatus === "Repeat Buyer" ||
      buyer.retentionStatus === "Loyal Buyer" ||
      buyer.retentionStatus === "VIP Buyer" ||
      buyer.retentionStatus === "High-Value Buyer"
  );

  const oneTimeBuyers = buyerRetention.filter(
    (buyer) => Number(buyer.auctionsWon || 0) === 1
  );

  const returningBuyerRate =
    buyerRetention.length > 0
      ? (repeatBuyers.length / buyerRetention.length) * 100
      : 0;

  const loyalBuyers = buyerRetention.filter(
    (buyer) => buyer.retentionStatus === "Loyal Buyer"
  );

  const vipBuyers = buyerRetention.filter(
    (buyer) => buyer.retentionStatus === "VIP Buyer"
  );

  const dormantBuyers = buyerRetention.filter(
    (buyer) => buyer.daysSinceLastWin !== null && buyer.daysSinceLastWin >= 30
  );

  const highValueBuyers = buyerRetention.filter(
    (buyer) => Number(buyer.totalWinningValue || 0) >= 50
  );

  const topBuyer = buyerRetention[0] || null;

  return (
    <>
      <section className="stats-grid">
        <div className="stat-card">
          <span>Total Buyers</span>
          <strong>{buyerRetention.length}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Buyers who won auctions in Global Date Range
          </small>
        </div>

        <div className="stat-card">
          <span>Repeat Buyers</span>
          <strong>{repeatBuyers.length}</strong>
        </div>

        <div className="stat-card">
          <span>One-Time Buyers</span>
          <strong>{oneTimeBuyers.length}</strong>
        </div>

        <div className="stat-card">
          <span>Returning Buyer Rate</span>
          <strong>{Number(returningBuyerRate || 0).toFixed(1)}%</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Repeat buyers / total buyers
          </small>
        </div>

        <div className="stat-card">
          <span>High-Value Buyers</span>
          <strong>{highValueBuyers.length}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Winning value 50 KWD or more
          </small>
        </div>

        <div className="stat-card">
          <span>Loyal Buyers</span>
          <strong>{loyalBuyers.length}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            5–9 auction wins
          </small>
        </div>

        <div className="stat-card">
          <span>VIP Buyers</span>
          <strong>{vipBuyers.length}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            10+ auction wins
          </small>
        </div>

        <div className="stat-card">
          <span>Dormant Buyers</span>
          <strong>{dormantBuyers.length}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            No wins for 30+ days
          </small>
        </div>

        <div className="stat-card">
          <span>Top Buyer</span>
          <strong>{topBuyer ? topBuyer.buyerName : "—"}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            {topBuyer ? `${topBuyer.auctionsWon} wins` : "—"}
          </small>
        </div>

        <div className="stat-card">
          <span>Total Winning Value</span>
          <strong>
            {formatKWD(
              buyerRetention.reduce(
                (sum, buyer) => sum + Number(buyer.totalWinningValue || 0),
                0
              )
            )}
          </strong>
        </div>
      </section>

      <section className="table-card">
        <div className="table-header">
          <div>
            <h2>Buyer Retention</h2>
            <p>
              Buyer quality, repeat behavior, winning value, and dormant buyer detection.
            </p>
          </div>
        </div>

        <div className="controls-row">
          <input
            className="search-input"
            placeholder="Search buyer username or email..."
            value={buyerSearch}
            onChange={(event) => setBuyerSearch(event.target.value)}
          />
        </div>

        {filteredBuyers.length === 0 ? (
          <p className="empty-text">No buyer retention data found.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Buyer</th>
                  <th>Email</th>
                  <th>Auctions Won</th>
                  <th>Total Winning Value</th>
                  <th>Highest Winning Bid</th>
                  <th>Latest Won Auction</th>
                  <th>First Win</th>
                  <th>Last Win</th>
                  <th>Days Since Last Win</th>
                  <th>Retention Status</th>
                </tr>
              </thead>

              <tbody>
                {filteredBuyers.map((buyer) => (
                  <tr key={buyer.buyerKey}>
                    <td>{buyer.buyerName || "—"}</td>
                    <td>{buyer.email || "—"}</td>
                    <td>{Number(buyer.auctionsWon || 0)}</td>
                    <td>{formatKWD(buyer.totalWinningValue)}</td>
                    <td>{formatKWD(buyer.highestWinningBid)}</td>
                    <td>{buyer.latestWonAuction || "—"}</td>
                    <td>{formatDate(buyer.firstWinDate)}</td>
                    <td>{formatDate(buyer.lastWinDate)}</td>
                    <td>
                      {buyer.daysSinceLastWin === null
                        ? "—"
                        : buyer.daysSinceLastWin}
                    </td>
                    <td>
                      <span className="badge reviewed">
                        {buyer.retentionStatus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

export default BuyerRetentionPage;